import React, { useCallback, useMemo } from 'react';
import {
  Box,
  RawAvatar,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { LetterIconSizeEnum, OrganizationTypes } from '@/common/constants';
import { UserLetterIcon } from '@/components/elements/LetterIcon/UserLetterIcon';
import { StatusBlock } from '@/components/elements/StatusBlock';
import { Topbar } from '@/modules/core/components';
import { useProgressScrollState } from '@/modules/core/hooks/useScrollSidebar';
import { MODULES } from '@/modules/employee/Employee/EmployeeSidebar/hooks/useEmployeeModules';
import { EnrollmentModal } from '@/modules/employee/Employee/EnrollmentModal';
import MfaStatusBlock from '@/modules/employee/Employee/RegistrationAndLogin/Security/MfaUnlock/MfaStatusBlock';
import { useSecurityStore } from '@/modules/employee/Employee/RegistrationAndLogin/Security/stores';
import { useUserAccountsLockStore } from '@/modules/employee/Employee/RegistrationAndLogin/UserAccountsSecurity/stores';
import theme from '@/styles/theme';

import { useEmployeeToolbarStore } from './stores';

const AnimatedAvatar = styled(RawAvatar)`
  transition: all 0.35s;
`;

interface Props {
  hasSubmitClaimButton?: boolean;
  isEditMode?: boolean;
  scrollToSection: (data: { module?: string, section?: string }) => void;
  leadingActions?: React.ReactNode;
  children?: React.ReactNode;
}

const EmployeeTopbar: React.FC<Props> = ({
  hasSubmitClaimButton,
  isEditMode,
  scrollToSection,
  leadingActions,
  children,
}) => {
  const { hideHeader } = useProgressScrollState();
  const currentState = useEmployeeToolbarStore((state) => state.sourceState);
  const isLoginLocked = useSecurityStore((state) => state.state.isLoginLocked);
  const isMfaLocked = useSecurityStore((state) => state.state.isMfaLocked);
  const hasLinkedLockedByAdmin = useUserAccountsLockStore((state) => state.state.hasLockedByAdmin);
  const hasLinkedLockedByMfa = useUserAccountsLockStore((state) => state.state.hasLockedByMfa);
  const showLockedByAdmin = isLoginLocked || hasLinkedLockedByAdmin;
  const showLinkedLockedByMfa = !isMfaLocked && hasLinkedLockedByMfa;

  const handleScrollToRegistrationAndLogin = useCallback(() => {
    scrollToSection({ module: MODULES.REGISTRATION_AND_LOGIN });
  }, [scrollToSection]);

  const employeeName = useMemo(() => `${currentState?.fullName || ''}`, [currentState]);
  return (
    <Box border={{ side: 'bottom', color: 'border2', size: '1px' }}>
      <Box
        data-testid={hideHeader ? 'minified-topbar' : 'expanded-topbar'}
        height={{ min: 'max-content' }}
      >
        <Topbar />
      </Box>
      <Box
        alignSelf="center"
        pad={{ horizontal: '40px' }} 
      >
        <Box
          width={theme.defaultContentWidth}
          direction="row"
          justify="between"
          alignSelf="center"
          align="center"
          height={theme.spacings.spacing72}
        >
          <Box
            direction="row"
            align="center"
            margin={{ horizontal: 'spacing8' }}
          >
            {
              currentState?.avatar ? (
                <AnimatedAvatar size='48px' src={currentState?.avatar} />
              ) : (
                <UserLetterIcon
                  size={LetterIconSizeEnum.LOGO}
                  text={employeeName}
                  type={OrganizationTypes.employee}
                  sizePx='48'
                />
              )
            }
            <Text
              data-testId="top_bar_employee_name"
              size="2xl"
              weight="bold"
              margin={{ left: 'spacing16', right: 'spacing8' }}
              title={employeeName}
              truncate
            >
              {employeeName}
            </Text>
            {showLockedByAdmin && (
              <StatusBlock
                testId="top_bar_locked-by-admin"
                title="Locked by admin"
                backgroundColor="danger"
                margin={{ left: 'spacing8' }}
                onClick={handleScrollToRegistrationAndLogin}
              />
            )}
            {isMfaLocked && (
              <MfaStatusBlock
                margin={{ left: 'spacing8' }}
                onClick={handleScrollToRegistrationAndLogin}
              />
            )}
            {showLinkedLockedByMfa && (
              <StatusBlock
                testId="top_bar_locked-by-mfa"
                title="Locked by MFA"
                backgroundColor="danger"
                margin={{ left: 'spacing8' }}
                onClick={handleScrollToRegistrationAndLogin}
              />
            )}
          </Box>
          <Box direction="row" width={{ min: 'auto' }} align="center">
            {leadingActions && (
              <Box direction="row" align="center" margin={{ right: 'spacing12' }}>
                {leadingActions}
              </Box>
            )}
            {!isEditMode ? (
              <EnrollmentModal
                hasSubmitClaimButton={hasSubmitClaimButton}
              >
                {children}
              </EnrollmentModal>
            ) : (
              <>
                {children}
              </>
            )}

            {/* {children} */}
            {/* <Button size="medium" secondary>
          <Box justify="center" align="center" width="smallControl">
            Claims
          </Box>
        </Button>
        <Button size="medium" primary>

          <Box direction="row" justify="center" align="center" pad={{ left: 'spacing16', right: 'spacing24' }}>

            <Box margin={{ right: 'spacing4' }}>
              <SearchUserIcon color="iconOnColor" />
            </Box>
            Participant site
          </Box>
        </Button> */}

          </Box>

        </Box>
      </Box>
    </Box>
  );
};
export default EmployeeTopbar;
